import { useState, useEffect } from "react";
import api, { formatCurrency } from "@/lib/api";
import { CheckCircle, XCircle, Clock, Brain, RefreshCw, Search } from "lucide-react";

export default function ReassessmentsPage() {
  const [reassessments, setReassessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const { data } = await api.get("/reassessments", { params });
      setReassessments(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const handleDecision = async (id, decision) => {
    try {
      await api.post(`/reassessments/${id}/approve`, { decision, comment: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const runAiScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const { data } = await api.post("/reassessments/ai-scan", {});
      setScanResult(data);
      load(); // Refresh list
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setScanning(false); }
  };

  return (
    <div className="space-y-6" data-testid="reassessments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Reassessments</h1>
          <p className="text-sm text-[#6B7280] mt-1">Useful-life reassessment proposals — manual or AI-generated</p>
        </div>
        <div className="flex gap-3">
          <select className="stratum-input" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="reassessment-filter">
            <option value="">All</option>
            <option value="OPEN">Open</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <button onClick={runAiScan} disabled={scanning} className="stratum-btn-primary flex items-center gap-2" data-testid="ai-scan-btn">
            <Brain size={14} /> {scanning ? "Scanning all assets..." : "AI Scan All Assets"}
          </button>
        </div>
      </div>

      {/* AI Scan Result */}
      {scanResult && (
        <div className="stratum-card p-6 border-l-4 border-l-[#C8A961]">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={18} className="text-[#C8A961]" />
            <h3 className="font-display text-base font-semibold">AI Reassessment Scan Complete</h3>
          </div>
          <div className="grid grid-cols-4 gap-4 mb-4 text-sm">
            <div className="p-3 rounded-lg bg-[#FAF8F4]">
              <div className="font-mono text-lg font-semibold tabular-nums">{scanResult.summary.total_assets_scanned}</div>
              <div className="text-xs text-[#6B7280]">Assets scanned</div>
            </div>
            <div className="p-3 rounded-lg bg-[#FAF8F4]">
              <div className="font-mono text-lg font-semibold tabular-nums text-[#C8A961]">{scanResult.proposals_created}</div>
              <div className="text-xs text-[#6B7280]">Proposals created</div>
            </div>
            <div className="p-3 rounded-lg bg-[#FAF8F4]">
              <div className="font-mono text-lg font-semibold tabular-nums">{scanResult.summary.not_in_use}</div>
              <div className="text-xs text-[#6B7280]">Not in use</div>
            </div>
            <div className="p-3 rounded-lg bg-[#FAF8F4]">
              <div className="font-mono text-lg font-semibold tabular-nums">{scanResult.summary.past_useful_life}</div>
              <div className="text-xs text-[#6B7280]">Past useful life</div>
            </div>
          </div>
          {scanResult.ai_commentary?.length > 0 && (
            <div className="p-4 rounded-lg bg-[#0B1020]/5 text-sm text-[#1A1A1A]">
              <span className="text-xs text-[#C8A961] font-medium uppercase">AI Commentary:</span>
              <p className="mt-1 whitespace-pre-wrap">{scanResult.ai_commentary[0]?.commentary}</p>
            </div>
          )}
        </div>
      )}

      {/* Reassessment List */}
      {loading ? (
        <div className="stratum-card p-8 text-center text-[#6B7280]">Loading...</div>
      ) : reassessments.length === 0 ? (
        <div className="stratum-card p-12 text-center">
          <Clock size={32} className="mx-auto text-[#C8A961] mb-3" />
          <p className="text-[#6B7280]">No reassessment proposals yet. Click "AI Scan All Assets" to generate proposals based on usage patterns, age, and policy alignment.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reassessments.map(r => (
            <div key={r.id} className={`stratum-card p-5 ${r.severity === "warn" ? "border-l-4 border-l-[#92400E]" : ""}`} data-testid={`reassessment-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-[#0B1020]">{r.headline || r.asset_tag}</h4>
                    {r.in_use === false && <span className="px-1.5 py-0.5 rounded bg-[#991B1B]/10 text-[#991B1B] text-[10px] font-medium">NOT IN USE</span>}
                    {r.source === "AI_SCAN" && <span className="px-1.5 py-0.5 rounded bg-[#C8A961]/10 text-[#C8A961] text-[10px] font-medium">AI</span>}
                  </div>
                  <p className="text-sm text-[#6B7280] mt-1">{r.rationale}</p>
                  <div className="flex gap-4 mt-2 text-xs text-[#6B7280]">
                    <span>Entity: {r.entity_name || "—"}</span>
                    <span>Category: {r.category_name || "—"}</span>
                    <span>Current: {r.current_life_months} mo → Proposed: {r.proposed_life_months} mo</span>
                    <span>Carrying: {formatCurrency(r.carrying_amount)}</span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  {r.status === "OPEN" && (
                    <>
                      <button onClick={() => handleDecision(r.id, "APPROVED")} className="px-3 py-1.5 text-xs rounded-lg bg-[#065F46]/10 text-[#065F46] hover:bg-[#065F46]/20" data-testid={`approve-reass-${r.id}`}>Approve</button>
                      <button onClick={() => handleDecision(r.id, "REJECTED")} className="px-3 py-1.5 text-xs rounded-lg bg-[#991B1B]/10 text-[#991B1B] hover:bg-[#991B1B]/20" data-testid={`reject-reass-${r.id}`}>Reject</button>
                    </>
                  )}
                  {r.status === "APPROVED" && <span className="flex items-center gap-1 text-xs text-[#065F46]"><CheckCircle size={14} /> Approved</span>}
                  {r.status === "REJECTED" && <span className="flex items-center gap-1 text-xs text-[#991B1B]"><XCircle size={14} /> Rejected</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
